import { format } from "date-fns";
import { useLocalStorageState } from "../hooks/localStorage";
import { useFormik } from "formik";
import { useEffect } from "react";
import { ProjectID } from "./project";
import { match } from "ts-pattern";

export interface DashboardViewFormValues {
  sites: string[];
  analyte: string | null;
  startDate: string;
  endDate: string;
}

export type DashboardStorageKey = "groundWaterView" | "surfaceWaterView" | "drinkingWaterView";

const initialFormValues: DashboardViewFormValues = {
  sites: [],
  analyte: null,
  startDate: "2018-01-28",
  endDate: format(new Date(), "yyyy-MM-dd"),
};

export function useDashboardStorageData(project: ProjectID) {
  const key = projectToKey(project);
  return useLocalStorageState({ key, value: { ...initialFormValues, ...projectDefaultForm(project) } });
}

export function useDashboardForm(project: ProjectID) {
  const [initialValues, handleUpdateForm] = useDashboardStorageData(project);

  const formik = useFormik({
    initialValues,
    onSubmit: (_) => {},
  });

  useEffect(() => {
    handleUpdateForm(formik.values);
  }, [formik.values, handleUpdateForm]);

  return formik;
}

function projectToKey(project: ProjectID): DashboardStorageKey {
  return match<ProjectID, DashboardStorageKey>(project)
    .with("groundWater", () => "groundWaterView")
    .with("surfaceWater", () => "surfaceWaterView")
    .with("drinkingWater", () => "drinkingWaterView")
    .exhaustive();
}

function projectDefaultForm(project: ProjectID): Partial<DashboardViewFormValues> {
  return match<ProjectID, Partial<DashboardViewFormValues>>(project)
    .with("groundWater", () => ({
      sites: ["GWMW1", "GWMW2", "GWMW3"],
      analyte: "Total Dissolved Solids",
    }))
    .with("surfaceWater", () => ({
      sites: ["SWRRSLRW-1", "SWRRSLRE-4"],
      analyte: "Total Nitrogen",
    }))
    .with("drinkingWater", () => ({
      sites: ["EP002 (NS2)", "EP003 (NS3)", "EP004 (NS4)"],
      analyte: "Nitrate as N",
    }))
    .exhaustive();
}
